import React, { useMemo, useState } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Select } from '@/components/ui/catalyst/select';

import type { ExternalCmsType } from '../../services/api/generated/taskImportRunsService';
import { taskImportRunsApiClient } from '../../services/api/taskImportRunsService';
import { DragDropFileInput } from '../demand-example/DragDropFileInput';
import { FirmCombobox } from '../firms/FirmCombobox';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  defaultFirmId?: string;
};

export const UploadRunDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  defaultFirmId,
}) => {
  const [firmId, setFirmId] = useState<string>(defaultFirmId ?? '');
  const [cmsType, setCmsType] = useState<ExternalCmsType>('filevine');
  const [file, setFile] = useState<File | null>(null);

  const { data: guessQuery, isFetching: guessing } =
    taskImportRunsApiClient.useTaskImportRunsGuessFirmCmsQuery(
      { firmId },
      { skip: !firmId },
    );

  const [createRun, { isLoading }] =
    taskImportRunsApiClient.useTaskImportRunsCreateMutation();

  const guessedCms = useMemo(() => guessQuery?.cms_type ?? null, [guessQuery]);

  const handleSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    if (!firmId || !file) return;
    await createRun({
      createTaskImportRun: {
        firm_id: firmId,
        cms_type: guessedCms ?? cmsType,
        file,
      },
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} size="lg">
      <form onSubmit={handleSubmit} aria-label="Upload CMS report">
        <DialogBody>
          <DialogTitle>Upload External CMS Report</DialogTitle>
          <DialogDescription>
            Choose a firm, CMS type, and upload a CSV/XLSX export.
          </DialogDescription>

          <div className="mt-4">
            <span id="firm_label" className="mb-1 block text-sm font-medium">
              Firm
            </span>
            <div aria-labelledby="firm_label">
              <FirmCombobox
                value={firmId}
                onChange={setFirmId}
                className="w-full"
                panelClassName="z-50"
              />
            </div>
          </div>

          <div className="mt-3">
            <span
              id="cms_type_label"
              className="mb-1 block text-sm font-medium"
            >
              CMS Type
            </span>
            <Select
              id="cms_type"
              name="cms_type"
              aria-labelledby="cms_type_label"
              value={cmsType}
              onChange={(e) =>
                setCmsType(
                  (e.target as HTMLSelectElement).value as ExternalCmsType,
                )
              }
            >
              <option value="filevine">Filevine</option>
              <option value="clio">Clio</option>
            </Select>
            <p className="mt-1 text-xs text-gray-500">
              {guessing && 'Guessing CMS…'}
              {!guessing && guessedCms && `Guessed: ${guessedCms}`}
              {!guessing && !guessedCms && 'Enter manually if not detected.'}
            </p>
          </div>

          <div className="mt-4">
            <span className="mb-1 block text-sm font-medium">File</span>
            <DragDropFileInput
              selectedFile={file}
              onFileChange={(f) => setFile(f)}
              className="w-full"
              accept=".csv, .xlsx, text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              idleTitle="Upload CSV or Excel file"
              draggingTitle="Drop your report here"
              subtitle="Drag and drop a CSV or Excel file here, or click to browse"
            />
            <p className="mt-1 text-xs text-gray-500">
              CSV or Excel. See Notion for export instructions.
            </p>
          </div>

          <DialogActions>
            <Button color="zinc" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="blue"
              type="submit"
              disabled={!firmId || !file || isLoading}
            >
              {isLoading ? 'Uploading…' : 'Start Run'}
            </Button>
          </DialogActions>
        </DialogBody>
      </form>
    </Dialog>
  );
};
